import { useState, useEffect, useContext } from 'react';
import { Table, Button, Spin, Pagination, message, Drawer, Form, Input, DatePicker, Space, Select, InputNumber, Modal } from 'antd';
import axios from 'axios';
import { LoginContext } from '../../../context/loginContext';
import CNuevoMovimientoAdministrador from './movimiento-administrador.js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FilterOutlined, DeleteOutlined } from '@ant-design/icons';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('America/Argentina/Buenos_Aires');

const { RangePicker } = DatePicker;
const { Option } = Select;

const CListaMovimientosAdministrador = () => {
    const [movimientos, setMovimientos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [editingMovimientoId, setEditingMovimientoId] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [filtros, setFiltros] = useState({});
    const [form] = Form.useForm();
    const pageSize = 10;
    const { user, selectedEmpresa } = useContext(LoginContext);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [exportForm] = Form.useForm();
    const [exportLoading, setExportLoading] = useState(false);

    useEffect(() => {
        fetchMovimientos();
        fetchDocumentTypes();
    }, [currentPage, filtros]);

    const fetchMovimientos = async () => {
        setLoading(true);
        try {
            // Construir la URL con los filtros
            let url = `${process.env.REACT_APP_API_URL}/movimiento/empresa/${selectedEmpresa.emp_id}?page=${currentPage}&limit=${pageSize}`;

            // Añadir filtros a la URL si existen
            if (filtros.fecha_desde) {
                url += `&fecha_desde=${filtros.fecha_desde}`;
            }
            if (filtros.fecha_hasta) {
                url += `&fecha_hasta=${filtros.fecha_hasta}`;
            }
            if (filtros.cuenta) {
                url += `&cuenta=${filtros.cuenta}`;
            }
            if (filtros.tipo_comprobante) {
                url += `&tipo_comprobante=${filtros.tipo_comprobante}`;
            }
            if (filtros.libroIVA) {
                url += `&libroIVA=${filtros.libroIVA}`;
            }
            if (filtros.total_min) {
                url += `&total_min=${filtros.total_min}`;
            }
            if (filtros.total_max) {
                url += `&total_max=${filtros.total_max}`;
            }
            if (filtros.comprobante_completo) {
                url += `&comprobante_completo=${filtros.comprobante_completo}`;
            }

            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const response = await axios.get(url, config);

            const { movimientos, pagination } = response.data;
            console.log('Movimientos', movimientos);

            // Mapear datos para que coincidan con la tabla
            const formattedMovimientos = movimientos.map(mov => ({
                key: mov.mov_id,
                fecha: dayjs.utc(mov.mov_fecha).tz('America/Argentina/Buenos_Aires', true).format('DD/MM/YYYY'),
                cuenta: mov.cue_nombre || 'Sin descripción',
                comprobante: `${mov.mov_ptovta}-${mov.mov_nrocomp}`,
                monto: `$${parseFloat(mov.mov_total).toFixed(2)}`,
                tipo: mov.iva_nombre,
                mov_id: mov.mov_id // Guardar el ID para la edición
            }));

            setMovimientos(formattedMovimientos);
            setTotalItems(pagination.totalItems);
        } catch (error) {
            // Manejar específicamente el error 404 (No se encontraron movimientos)
            if (error.response && error.response.status === 404) {
                // Mostrar el mensaje que viene del servidor
                const errorMessage = error.response.data.message || 'No se encontraron movimientos para esta empresa';

                // Utilizar notification en lugar de message para un mensaje más visible
                message.error(errorMessage);

                // Limpiar los datos de movimientos y establecer el total a cero
                setMovimientos([]);
                setTotalItems(0);
            } else {
                // Para otros errores, mostrar el mensaje genérico
                message.error('Error al cargar los movimientos');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchDocumentTypes = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/tipos-comprobantes`;
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const response = await axios.get(url, config);
            setDocumentTypes(response.data);
        } catch (error) {
            console.error('Error fetching document types:', error);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleEditClick = (movimientoId) => {
        setEditingMovimientoId(movimientoId);
    };

    const handleEditCancel = () => {
        setEditingMovimientoId(null);
        fetchMovimientos();
    };

    const handleEditSuccess = () => {
        setEditingMovimientoId(null);
        fetchMovimientos(); // Recargar los datos después de editar
    };

    const showDrawer = () => {
        setDrawerVisible(true);
    };

    const onClose = () => {
        setDrawerVisible(false);
    };

    const handleApplyFilters = (values) => {
        const nuevosFiltros = {};

        // Procesar fechas
        if (values.fechaRango && values.fechaRango.length === 2) {
            nuevosFiltros.fecha_desde = values.fechaRango[0].format('YYYY-MM-DD');
            nuevosFiltros.fecha_hasta = values.fechaRango[1].format('YYYY-MM-DD');
        }

        // Procesar resto de filtros
        if (values.cuenta) nuevosFiltros.cuenta = values.cuenta;
        if (values.tipo_comprobante) nuevosFiltros.tipo_comprobante = values.tipo_comprobante;
        if (values.libroIVA) nuevosFiltros.libroIVA = values.libroIVA;
        if (values.total_min) nuevosFiltros.total_min = values.total_min;
        if (values.total_max) nuevosFiltros.total_max = values.total_max;
        if (values.comprobante_completo) {
            nuevosFiltros.comprobante_completo = values.comprobante_completo;
        }

        setFiltros(nuevosFiltros);
        setCurrentPage(1); // Resetear a la primera página con nuevos filtros
        onClose();
    };

    const handleResetFilters = () => {
        form.resetFields();
        setFiltros({});
        setCurrentPage(1);
        onClose();
    };

    const showExportModal = () => {
        setExportModalVisible(true);
    };

    const closeExportModal = () => {
        setExportModalVisible(false);
        exportForm.resetFields();
    };

    const handleDelete = (movimientoId) => {
        Modal.confirm({
            title: '¿Estás seguro de que querés eliminar esta factura?',
            content: 'Esta acción no se puede deshacer.',
            okText: 'Sí, eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/movimiento/${movimientoId}`,
                        {
                            method: 'DELETE',
                            headers: {
                                Authorization: `Bearer ${user.token}`,
                            },
                        }
                    );

                    if (!response.ok) {
                        throw new Error('Error al eliminar el movimiento');
                    }

                    message.success('Movimiento eliminado correctamente');
                    fetchMovimientos();
                } catch (error) {
                    console.error(error);
                    message.error('Error al eliminar el movimiento');
                }
            },
        });
    };


    const handleExportIVA = async (values) => {
        setExportLoading(true);
        try {
            const fd = values.fechaExportar?.[0];
            const fh = values.fechaExportar?.[1];
            const libroIVA = values.libroIVA;

            const payload = {
                fechaDesde: fd.format('YYYY-MM-DD'),
                fechaHasta: fh.format('YYYY-MM-DD'),
                libroIVA,
                idEmpresa: selectedEmpresa.emp_id,
            };

            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                    'Content-Type': 'application/json',
                },
                responseType: 'blob', // necesario para descargar ZIP
                validateStatus: (status) => status >= 200 && status < 500, // para poder leer errores JSON en blob
            };

            const url = `${process.env.REACT_APP_API_URL}/arca/exportar-libro-iva`; // mismo endpoint, ahora devuelve ZIP
            const resp = await axios.post(url, payload, config);

            // Si el backend respondió error con JSON, axios lo trae como blob también.
            const ct = resp.headers['content-type'] || '';
            if (!resp.status || resp.status >= 400) {
                // Intentamos parsear mensaje de error
                if (ct.includes('application/json')) {
                    const text = await resp.data.text();
                    try {
                        const j = JSON.parse(text);
                        message.error(j.message || 'No se pudo generar el archivo.');
                    } catch {
                        message.error('No se pudo generar el archivo.');
                    }
                } else {
                    message.error('No se pudo generar el archivo.');
                }
                return;
            }

            // Descarga del ZIP
            const blob = new Blob([resp.data], { type: 'application/zip' });
            const link = document.createElement('a');
            const urlBlob = window.URL.createObjectURL(blob);
            link.href = urlBlob;

            // Nombre de archivo desde header o fallback
            let filename = 'libro-iva.zip';
            const cd = resp.headers['content-disposition'];
            if (cd) {
                const m = cd.match(/filename="?([^"]+)"?/i);
                if (m && m[1]) filename = m[1];
            } else {
                const yyyymm = fd.format('YYYYMM');
                filename = `LIBRO_IVA_${libroIVA === 1 ? 'COMPRAS' : 'VENTAS'}_${yyyymm}.zip`;
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlBlob);

            message.success('ZIP generado: contiene los 2 TXT para AFIP/ARCA.');
            closeExportModal();
        } catch (err) {
            console.error(err);
            message.error('Error al exportar el Libro IVA.');
        } finally {
            setExportLoading(false);
        }
    };


    const columns = [
        { title: 'Fecha', dataIndex: 'fecha', key: 'fecha' },
        { title: 'Cuenta', dataIndex: 'cuenta', key: 'cuenta' },
        { title: 'Comprobante', dataIndex: 'comprobante', key: 'comprobante' },
        { title: 'Monto', dataIndex: 'monto', key: 'monto' },
        { title: 'Tipo', dataIndex: 'tipo', key: 'tipo' },
        {
            title: 'Acciones',
            key: 'acciones',
            render: (_, record) => (
                <Space>
                    <Button type="primary" onClick={() => handleEditClick(record.mov_id)}>
                        Editar
                    </Button>
                    <Button
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.mov_id)}
                    />
                </Space>
            ),
        },

    ];

    // Si estamos editando un movimiento, mostrar la pantalla de edición
    if (editingMovimientoId) {
        return (
            <CNuevoMovimientoAdministrador
                idMovimiento={editingMovimientoId}
                onCancel={handleEditCancel} // Pasar la función para volver a la lista
                onSuccess={handleEditSuccess} // Pasar la función para actualizar la lista tras editar
            />
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Button
                    type="primary"
                    onClick={showExportModal}
                    style={{ marginRight: 8 }}
                >
                    Exportar Libro IVA Digital
                </Button>
                <Button
                    type="primary"
                    onClick={showDrawer}
                    icon={<FilterOutlined />}
                >
                    Filtros
                </Button>
            </div>

            {loading ? <Spin size="large" style={{ display: 'block', margin: '20px auto' }} /> : (
                <>
                    <Table
                        dataSource={movimientos}
                        columns={columns}
                        rowKey="key"
                        pagination={false}
                    />
                    <Pagination
                        current={currentPage}
                        total={totalItems}
                        pageSize={pageSize}
                        onChange={handlePageChange}
                        style={{ marginTop: 16, textAlign: 'center' }}
                    />
                </>
            )}

            <Drawer
                title="Filtros de movimientos"
                placement="right"
                width={400}
                onClose={onClose}
                open={drawerVisible}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Button onClick={handleResetFilters} style={{ marginRight: 8 }}>
                            Limpiar filtros
                        </Button>
                        <Button onClick={() => form.submit()} type="primary">
                            Aplicar filtros
                        </Button>
                    </div>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleApplyFilters}
                >
                    <Form.Item
                        name="fechaRango"
                        label="Rango de fechas"
                    >
                        <RangePicker
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                        />
                    </Form.Item>

                    <Form.Item
                        name="cuenta"
                        label="CUIT de cuenta"
                    >
                        <Input placeholder="Ingrese el CUIT de la cuenta" />
                    </Form.Item>

                    <Form.Item
                        name="tipo_comprobante"
                        label="Tipo de comprobante"
                    >
                        <Select placeholder="Seleccione tipo de comprobante" allowClear>
                            {documentTypes.map((type) => (
                                <Option key={type.tic_id} value={type.tic_id}>
                                    {type.tic_nombre}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="libroIVA"
                        label="Libro IVA"
                    >
                        <Select placeholder="Seleccione libro IVA" allowClear>
                            <Option value="1">Compras</Option>
                            <Option value="2">Ventas</Option>
                            {/* Añadir más opciones según los libros IVA disponibles */}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Monto total">
                        <Space style={{ display: 'flex' }}>
                            <Form.Item name="total_min" noStyle>
                                <InputNumber
                                    placeholder="Mínimo"
                                    style={{ width: '100%' }}
                                    min={0}
                                    prefix="$"
                                />
                            </Form.Item>
                            <span style={{ margin: '0 8px' }}>-</span>
                            <Form.Item name="total_max" noStyle>
                                <InputNumber
                                    placeholder="Máximo"
                                    style={{ width: '100%' }}
                                    min={0}
                                    prefix="$"
                                />
                            </Form.Item>
                        </Space>
                    </Form.Item>
                    <Form.Item
                        name="comprobante_completo"
                        label="Comprobante (PtoVta-Nro)"
                        rules={[
                            {
                                pattern: /^\d{5}-\d{8}$/,
                                message: 'Debe tener el formato 00002-00001234'
                            }
                        ]}
                    >
                        <Input
                            placeholder="Ej: 00002-00001234"
                            maxLength={14}
                            onChange={(e) => {
                                // Solo permitir números y un guion, sin autocompletar ceros
                                const cleaned = e.target.value.replace(/[^0-9\-]/g, '').slice(0, 14);
                                form.setFieldsValue({ comprobante_completo: cleaned });
                            }}
                        />
                    </Form.Item>


                </Form>
            </Drawer>
            {/* Modal para exportar libro IVA */}
            <Modal
                title="Exportar Libro IVA Digital"
                open={exportModalVisible}
                onCancel={closeExportModal}
                footer={[
                    <Button key="back" onClick={closeExportModal}>
                        Cancelar
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={exportLoading}
                        onClick={() => exportForm.submit()}
                    >
                        Exportar
                    </Button>,
                ]}
            >
                <Form
                    form={exportForm}
                    layout="vertical"
                    onFinish={handleExportIVA}
                >
                    <Form.Item
                        name="fechaExportar"
                        label="Rango de fechas"
                        rules={[{ required: true, message: 'Por favor seleccione un rango de fechas' }]}
                    >
                        <RangePicker
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                        />
                    </Form.Item>

                    <Form.Item
                        name="libroIVA"
                        label="Tipo de Libro IVA"
                        rules={[{ required: true, message: 'Por favor seleccione el tipo de Libro IVA' }]}
                    >
                        <Select placeholder="Seleccione el tipo de libro">
                            <Option value={1}>Libro IVA Compras</Option>
                            <Option value={2}>Libro IVA Ventas</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CListaMovimientosAdministrador;